<template>
  <div v-if="student" class="p-6">
    <div>
      <!-- 1. Page Header -->
      <div class="mb-8">
        <button @click="goBack" class="text-blue-600 hover:underline mb-4">&larr; Back to Test Details</button>
        <h1 class="text-3xl font-bold text-gray-800">Manage Work for {{ student.name }}</h1>
        <p class="text-gray-600">Upload new pages or view existing work for this student.</p>
      </div>

      <!-- 2. Uploader -->
      <div class="mb-8">
        <label
            class="flex flex-col items-center justify-center w-full h-48 border-2 border-dashed rounded-lg cursor-pointer transition-colors"
            :class="isDragging ? 'border-blue-500 bg-blue-50' : 'border-gray-300 bg-gray-50 hover:bg-gray-100'"
            @dragover.prevent="isDragging = true"
            @dragleave.prevent="isDragging = false"
            @drop.prevent="handleFileDrop"
        >
          <div class="flex flex-col items-center justify-center">
            <svg class="w-10 h-10 mb-3 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M7 16a4 4 0 01-4-4V6a4 4 0 014-4h10a4 4 0 014 4v6a4 4 0 01-4 4H7z"></path><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M10 9v6m3-3H7"></path></svg>
            <p class="mb-2 text-sm text-gray-500"><span class="font-semibold">Click or drop files to upload</span></p>
          </div>
          <input ref="fileInput" type="file" class="hidden" @change="handleFileSelect" multiple accept="image/png, image/jpeg" />
        </label>
        <div v-if="uploading" class="text-center mt-2 text-sm text-gray-500">Uploading {{ uploadProgress }}...</div>
      </div>

      <!-- 3. Page Overview -->
      <div>
        <h2 class="text-xl font-semibold mb-4 text-gray-700">Uploaded Pages</h2>
        <div v-if="studentPages.length === 0" class="text-center py-4 text-gray-500">
          No pages have been uploaded for this student yet.
        </div>
        <div v-else class="grid grid-cols-2 sm:grid-cols-4 md:grid-cols-6 lg:grid-cols-8 xl:grid-cols-10 gap-4">
          <div v-for="page in studentPages" :key="page.id" class="relative border rounded-lg overflow-hidden group">
            <button @click="viewPage(page.id)" class="w-full h-full">
              <img v-if="thumbnailUrls[page.id]" :src="thumbnailUrls[page.id]" class="w-full h-full object-cover" />
              <div v-else class="w-full h-24 flex items-center justify-center bg-gray-100">
                <svg class="animate-spin h-5 w-5 text-gray-400" fill="none" viewBox="0 0 24 24"><circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle><path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V4a10 10 0 00-10 10h2z"></path></svg>
              </div>
            </button>
            <button @click="deleteStudentPage(page.id)" class="absolute top-1 right-1 bg-red-600 text-white rounded-full p-1 transition-opacity">
              <svg class="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12" /></svg>
            </button>
          </div>
        </div>
      </div>
    </div>

    <!-- 4. Image Viewer Modal -->
    <transition name="fade">
      <div v-if="modalImageUrl" @click="modalImageUrl = null" class="fixed inset-0 bg-black bg-opacity-75 flex items-center justify-center z-50 p-4">
        <img :src="modalImageUrl" @click.stop class="max-w-full max-h-full object-contain" />
      </div>
    </transition>
  </div>
</template>

<script setup>
import { ref, onMounted, onBeforeUnmount } from 'vue'
import { useRouter } from 'vue-router'
import axios from '../services/axiosInstance'
import SockJS from 'sockjs-client'
import Stomp from 'stompjs'
import { useModalStore } from '@/stores/modal'

const props = defineProps({
  testId: { type: String, required: true },
  studentId: { type: String, required: true }
})

const router = useRouter()
const student = ref(null)
const studentPages = ref([])
const thumbnailUrls = ref({})
const isDragging = ref(false)
const fileInput = ref(null)
const uploading = ref(false)
const uploadProgress = ref('')
const stompClient = ref(null)
const modalImageUrl = ref(null)
const modal = useModalStore()

// --- DATA FETCHING ---
const fetchData = async () => {
  try {
    const [studentRes, pagesRes] = await Promise.all([
      axios.get(`/tests/${props.testId}/students/${props.studentId}`),
      axios.get(`/tests/${props.testId}/students/${props.studentId}/pages`)
    ])
    student.value = studentRes.data
    studentPages.value = pagesRes.data || []
    await fetchThumbnails()
  } catch (e) { console.error('Error fetching student data:', e) }
}

const fetchThumbnails = async () => {
  const promises = studentPages.value.map(async (page) => {
    try {
      const url = `/tests/${props.testId}/students/${props.studentId}/pages/${page.id}/thumbnail`
      const response = await axios.get(url, { responseType: 'blob' })
      thumbnailUrls.value[page.id] = URL.createObjectURL(response.data)
    } catch (error) { console.error(`Error fetching thumbnail for page ${page.id}:`, error) }
  })
  await Promise.all(promises)
}

// --- USER ACTIONS ---
const deleteStudentPage = async (pageId) => {
  const confirmed = await modal.confirm('Are you sure you want to delete this page?', 'Delete Page', 'Delete')
  if (confirmed) {
    try {
      await axios.delete(`/tests/${props.testId}/students/${props.studentId}/pages/${pageId}`)
      studentPages.value = studentPages.value.filter(p => p.id !== pageId)
      if (thumbnailUrls.value[pageId]) {
        URL.revokeObjectURL(thumbnailUrls.value[pageId])
        delete thumbnailUrls.value[pageId]
      }
    } catch (error) {
      console.error('Error deleting page:', error)
      modal.error('Failed to delete page. Please try again.')
    }
  }
}

const viewPage = async (pageId) => {
  try {
    const url = `/tests/${props.testId}/students/${props.studentId}/pages/${pageId}/file`
    const response = await axios.get(url, { responseType: 'blob' })
    modalImageUrl.value = URL.createObjectURL(response.data)
  } catch (error) {
    console.error('Error fetching full image:', error)
    modal.error('Could not load full image.')
  }
}

const goBack = () => router.push(`/tests/${props.testId}`)

// --- FILE UPLOAD LOGIC ---
const handleFileSelect = (e) => uploadFiles(e.target.files)
const handleFileDrop = (e) => uploadFiles(e.dataTransfer.files)

const uploadFiles = async (fileList) => {
  if (!fileList || fileList.length === 0) return
  uploading.value = true
  const filesToUpload = Array.from(fileList)
  for (let i = 0; i < filesToUpload.length; i++) {
    uploadProgress.value = `(${i + 1}/${filesToUpload.length})`
    const file = filesToUpload[i]
    const formData = new FormData()
    formData.append('file', file)
    try {
      await axios.post(`/tests/${props.testId}/students/${props.studentId}/upload`, formData)
    } catch (error) { console.error(`Upload error for ${file.name}:`, error) }
  }
  uploading.value = false
  uploadProgress.value = ''
  fetchData()
}

// --- WEBSOCKET LOGIC ---
const setupWebSocket = () => {
  const socket = new SockJS(process.env.VUE_APP_WS_URL)
  stompClient.value = Stomp.over(socket)
  stompClient.value.debug = null

  stompClient.value.connect({}, () => {
    const topic = `/topic/tests/${props.testId}/status`
    stompClient.value.subscribe(topic, (message) => {
      const body = JSON.parse(message.body)
      const studentId = body.student?.id
      if (!studentId || studentId !== props.studentId) return

      if (body.type === 'page_uploaded' || body.type === 'page_deleted') {
        fetchData()
      }
    })
  }, (error) => { console.error('[StudentWorkUpload] WebSocket connection error:', error) })
}

const disconnectWebSocket = () => {
  if (stompClient.value) {
    stompClient.value.disconnect(() => {})
  }
}

onMounted(() => {
  fetchData()
  setupWebSocket()
})

onBeforeUnmount(() => {
  disconnectWebSocket()
  Object.values(thumbnailUrls.value).forEach(url => URL.revokeObjectURL(url))
  if (modalImageUrl.value) {
    URL.revokeObjectURL(modalImageUrl.value)
  }
})
</script>

<style scoped>
.fade-enter-active, .fade-leave-active {
  transition: opacity 0.3s ease;
}
.fade-enter-from, .fade-leave-to {
  opacity: 0;
}
</style>
